// ---------------------------------------------------------------------------
// Prop LOD System — Quality-aware distance-based level of detail for props.
//
// Replaces the basic LOD in props.ts with a proper quality-preset-driven
// system. LOD0 = original mesh, LOD1 = simplified geometry (triangle
// decimation), LOD2 = bounding box proxy, beyond cull distance = hidden.
//
// Phase 10 Optimization — reduces triangle count at distance
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import type { GraphicsQuality } from '../core/settings';

// ---------------------------------------------------------------------------
// Configuration per quality preset
// ---------------------------------------------------------------------------

interface LODConfig {
  /** Distance thresholds [LOD1 start, LOD2 start, cull distance] in metres. */
  distances: [number, number, number];
  /** Simplification ratios [LOD1 triangle %, LOD2 is always box proxy]. */
  ratios: [number, number];
}

const QUALITY_LOD_CONFIGS: Record<GraphicsQuality, LODConfig> = {
  low:              { distances: [15, 30, 50],  ratios: [0.3, 0.1] },
  medium:           { distances: [20, 40, 70],  ratios: [0.4, 0.15] },
  high:             { distances: [25, 50, 100], ratios: [0.5, 0.2] },
  ultra:            { distances: [30, 60, 120], ratios: [0.5, 0.2] },
  'mobile-low':     { distances: [10, 20, 35],  ratios: [0.2, 0.05] },
  'mobile-medium':  { distances: [15, 30, 50],  ratios: [0.3, 0.1] },
  'mobile-high':    { distances: [20, 40, 70],  ratios: [0.4, 0.15] },
};

// ---------------------------------------------------------------------------
// PropLODSystem
// ---------------------------------------------------------------------------

export class PropLODSystem {
  private lodGroups: THREE.LOD[] = [];
  private config: LODConfig;

  constructor(quality: GraphicsQuality = 'high') {
    this.config = QUALITY_LOD_CONFIGS[quality] || QUALITY_LOD_CONFIGS.high;
  }

  /**
   * Create a LOD group from a high-detail mesh (or Group).
   *
   * LOD0 = original, LOD1 = simplified, LOD2 = bounding box, beyond = hidden
   *
   * @param obj - The full-detail mesh or group
   * @param colliderSize - Optional [w, h, d] for the bounding box proxy
   * @param customDistances - Optional per-prop [LOD1, LOD2, cull] distance overrides
   * @returns THREE.LOD wrapping the object with distance levels
   */
  createLOD(
    obj: THREE.Object3D,
    colliderSize?: [number, number, number] | null,
    customDistances?: [number, number, number],
  ): THREE.LOD {
    const lod = new THREE.LOD();
    const distances = customDistances || this.config.distances;
    const [d1, d2, d3] = distances;

    // LOD0: Original mesh at full detail
    lod.addLevel(obj, 0);

    // LOD1: Simplified geometry (decimated triangles)
    const lod1 = this.createSimplifiedLevel(obj, this.config.ratios[0]);
    if (lod1) {
      this.markVisualCollisionProxy(lod1);
      lod.addLevel(lod1, d1);
    }

    // LOD2: Very simple bounding box proxy
    const lod2 = this.createBoxProxy(obj, colliderSize);
    if (lod2) {
      this.markVisualCollisionProxy(lod2);
      lod.addLevel(lod2, d2);
    } else {
      console.warn('PropLODSystem: createBoxProxy returned null — LOD2 level skipped');
    }

    // Beyond d3: empty object (effectively culled)
    const empty = new THREE.Object3D();
    lod.addLevel(empty, d3);

    this.lodGroups.push(lod);
    return lod;
  }

  private markVisualCollisionProxy(obj: THREE.Object3D): void {
    obj.userData.cp_collision = false;
    obj.traverse((child) => {
      child.userData.cp_collision = false;
    });
  }

  /**
   * Create a simplified version of a mesh by decimating its index buffer.
   * For Groups, operates on each child mesh.
   */
  private createSimplifiedLevel(
    source: THREE.Object3D,
    ratio: number,
  ): THREE.Object3D | null {
    if (source instanceof THREE.Mesh) {
      return this.decimateMesh(source, ratio);
    }

    // For groups, clone the group structure and decimate each child mesh
    const group = new THREE.Group();
    let hasContent = false;

    source.traverse((child: THREE.Object3D) => {
      if (child === source) return;
      if (child instanceof THREE.Mesh) {
        const simplified = this.decimateMesh(child, ratio);
        if (simplified) {
          // Preserve local transform
          simplified.position.copy(child.position);
          simplified.rotation.copy(child.rotation);
          simplified.scale.copy(child.scale);
          group.add(simplified);
          hasContent = true;
        }
      }
    });

    return hasContent ? group : null;
  }

  /**
   * Decimate a mesh using vertex-clustering via spatial hashing.
   *
   * Groups vertices into grid cells and replaces each cluster with its
   * centroid. This preserves silhouette edges while reducing interior
   * complexity — much better visual quality than stride-based index skipping.
   *
   * Grid cell size is derived from the bounding box diagonal scaled by the
   * reduction ratio. Lower ratio = larger cells = more aggressive merging.
   */
  private decimateMesh(source: THREE.Mesh, ratio: number): THREE.Mesh | null {
    const srcGeo = source.geometry;
    const posAttr = srcGeo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!posAttr) return null;

    const vertCount = posAttr.count;
    if (vertCount === 0) return null;

    // --- Compute bounding box to determine cell size ---
    srcGeo.computeBoundingBox();
    const bbox = srcGeo.boundingBox!;
    const bboxSize = new THREE.Vector3();
    bbox.getSize(bboxSize);
    const diagonal = bboxSize.length();

    if (diagonal < 1e-6) return null;

    // Cell size: larger cells → more aggressive merging.
    // ratio 0.5 → cellSize = diagonal / 10, ratio 0.2 → diagonal / 4, etc.
    const cellSize = diagonal / Math.max(1, ratio * 20);
    const invCellSize = 1 / cellSize;

    // --- Build spatial hash: cell key → { centroid sum, count, new index } ---
    const cellMap = new Map<string, { sx: number; sy: number; sz: number; count: number; newIdx: number }>();
    // Remap each original vertex index to the cluster's merged vertex index
    const vertexRemap = new Int32Array(vertCount);
    let nextIdx = 0;

    const bboxMinX = bbox.min.x;
    const bboxMinY = bbox.min.y;
    const bboxMinZ = bbox.min.z;

    for (let i = 0; i < vertCount; i++) {
      const vx = posAttr.getX(i);
      const vy = posAttr.getY(i);
      const vz = posAttr.getZ(i);

      // Grid cell coordinates
      const cx = Math.floor((vx - bboxMinX) * invCellSize);
      const cy = Math.floor((vy - bboxMinY) * invCellSize);
      const cz = Math.floor((vz - bboxMinZ) * invCellSize);
      const key = `${cx},${cy},${cz}`;

      let cell = cellMap.get(key);
      if (!cell) {
        cell = { sx: 0, sy: 0, sz: 0, count: 0, newIdx: nextIdx++ };
        cellMap.set(key, cell);
      }
      cell.sx += vx;
      cell.sy += vy;
      cell.sz += vz;
      cell.count++;
      vertexRemap[i] = cell.newIdx;
    }

    const mergedCount = nextIdx;
    if (mergedCount === 0) return null;

    // --- Build merged position buffer from cell centroids ---
    const mergedPositions = new Float32Array(mergedCount * 3);
    for (const cell of cellMap.values()) {
      const inv = 1 / cell.count;
      const off = cell.newIdx * 3;
      mergedPositions[off] = cell.sx * inv;
      mergedPositions[off + 1] = cell.sy * inv;
      mergedPositions[off + 2] = cell.sz * inv;
    }

    // --- Remap index buffer and discard degenerate triangles ---
    const srcIndex = srcGeo.index;
    const newIndices: number[] = [];

    if (srcIndex) {
      const idxArr = srcIndex.array;
      for (let t = 0; t < idxArr.length; t += 3) {
        const a = vertexRemap[idxArr[t] as number];
        const b = vertexRemap[idxArr[t + 1] as number];
        const c = vertexRemap[idxArr[t + 2] as number];
        // Skip degenerate triangles (all three vertices merged into same cells)
        if (a !== b && b !== c && a !== c) {
          newIndices.push(a, b, c);
        }
      }
    } else {
      // Non-indexed geometry — treat every 3 vertices as a triangle
      for (let t = 0; t < vertCount; t += 3) {
        const a = vertexRemap[t];
        const b = vertexRemap[t + 1];
        const c = vertexRemap[t + 2];
        if (a !== b && b !== c && a !== c) {
          newIndices.push(a, b, c);
        }
      }
    }

    if (newIndices.length === 0) return null;

    // --- Build the simplified geometry ---
    const newGeo = new THREE.BufferGeometry();
    newGeo.setAttribute('position', new THREE.BufferAttribute(mergedPositions, 3));
    newGeo.setIndex(newIndices);

    // Transfer normals if present — average per cluster
    const normAttr = srcGeo.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (normAttr) {
      const mergedNormals = new Float32Array(mergedCount * 3);
      for (let i = 0; i < vertCount; i++) {
        const mi = vertexRemap[i] * 3;
        mergedNormals[mi] += normAttr.getX(i);
        mergedNormals[mi + 1] += normAttr.getY(i);
        mergedNormals[mi + 2] += normAttr.getZ(i);
      }
      // Normalize the averaged normals
      const nv = new THREE.Vector3();
      for (let i = 0; i < mergedCount; i++) {
        nv.set(mergedNormals[i * 3], mergedNormals[i * 3 + 1], mergedNormals[i * 3 + 2]);
        nv.normalize();
        mergedNormals[i * 3] = nv.x;
        mergedNormals[i * 3 + 1] = nv.y;
        mergedNormals[i * 3 + 2] = nv.z;
      }
      newGeo.setAttribute('normal', new THREE.BufferAttribute(mergedNormals, 3));
    } else {
      newGeo.computeVertexNormals();
    }

    // Transfer UVs if present — average per cluster
    const uvAttr = srcGeo.getAttribute('uv') as THREE.BufferAttribute | undefined;
    if (uvAttr) {
      const mergedUVs = new Float32Array(mergedCount * 2);
      const uvCounts = new Float32Array(mergedCount);
      for (let i = 0; i < vertCount; i++) {
        const mi = vertexRemap[i];
        mergedUVs[mi * 2] += uvAttr.getX(i);
        mergedUVs[mi * 2 + 1] += uvAttr.getY(i);
        uvCounts[mi]++;
      }
      for (let i = 0; i < mergedCount; i++) {
        if (uvCounts[i] > 1) {
          mergedUVs[i * 2] /= uvCounts[i];
          mergedUVs[i * 2 + 1] /= uvCounts[i];
        }
      }
      newGeo.setAttribute('uv', new THREE.BufferAttribute(mergedUVs, 2));
    }

    const mesh = new THREE.Mesh(newGeo, source.material);
    mesh.castShadow = source.castShadow;
    mesh.receiveShadow = source.receiveShadow;
    return mesh;
  }

  /**
   * Create a bounding-box proxy mesh for LOD2.
   *
   * Always computes the actual bounding box from source geometry for accurate
   * dimensions, falling back to colliderSize only when the source has no
   * measurable bounds. Creates a simple averaged-color material from all
   * source meshes for better visual continuity at distance.
   */
  private createBoxProxy(
    source: THREE.Object3D,
    colliderSize?: [number, number, number] | null,
  ): THREE.Mesh | null {
    let size: THREE.Vector3;
    let center: THREE.Vector3;

    // Always try to compute actual bounds from source geometry first
    const box = new THREE.Box3().setFromObject(source);
    const computedSize = new THREE.Vector3();
    if (!box.isEmpty()) {
      box.getSize(computedSize);
    }

    const hasValidBounds = !box.isEmpty()
      && computedSize.x > 0.01
      && computedSize.y > 0.01
      && computedSize.z > 0.01;

    if (hasValidBounds) {
      size = computedSize;
      center = new THREE.Vector3();
      box.getCenter(center);
    } else if (colliderSize) {
      size = new THREE.Vector3(colliderSize[0], colliderSize[1], colliderSize[2]);
      center = new THREE.Vector3(0, colliderSize[1] / 2, 0);
    } else {
      return null;
    }

    const geo = new THREE.BoxGeometry(size.x, size.y, size.z);
    geo.translate(center.x, center.y, center.z);

    // Use the dominant material color for visual continuity at distance
    const material = this.createDominantMaterial(source);

    const mesh = new THREE.Mesh(geo, material);
    mesh.castShadow = false; // Save shadow passes on LOD2
    mesh.receiveShadow = true;
    return mesh;
  }

  /**
   * Create a simple material whose color is the dominant color from the source
   * object — i.e. the color of the material used by the mesh with the most
   * vertices. This reduces visual pop when transitioning to LOD2 compared to
   * averaging all material colors.
   */
  private createDominantMaterial(source: THREE.Object3D): THREE.Material {
    let dominantColor = 0x999999;
    let maxVertices = 0;

    source.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        const count = child.geometry.getAttribute('position')?.count ?? 0;
        if (count > maxVertices) {
          maxVertices = count;
          const mat = Array.isArray(child.material) ? child.material[0] : child.material;
          if (mat instanceof THREE.MeshStandardMaterial) {
            dominantColor = mat.color.getHex();
          }
        }
      }
    });

    return new THREE.MeshStandardMaterial({
      color: dominantColor,
      roughness: 0.85,
    });
  }

  /**
   * Update quality preset — adjusts all LOD distances.
   */
  setQuality(quality: GraphicsQuality): void {
    this.config = QUALITY_LOD_CONFIGS[quality] || QUALITY_LOD_CONFIGS.high;

    // Update distances on existing LOD groups (bounds-check each level index)
    for (const lod of this.lodGroups) {
      const levels = lod.levels;
      if (levels.length >= 4) {
        if (levels.length > 1) levels[1].distance = this.config.distances[0];
        if (levels.length > 2) levels[2].distance = this.config.distances[1];
        if (levels.length > 3) levels[3].distance = this.config.distances[2];
      } else if (levels.length >= 3) {
        // Might be missing LOD1 — assign remaining distances safely
        if (levels.length > 1) levels[1].distance = this.config.distances[1];
        if (levels.length > 2) levels[2].distance = this.config.distances[2];
      }
    }
  }

  /**
   * Update all LOD groups. Call each frame with the active camera.
   */
  update(camera: THREE.Camera): void {
    for (const lod of this.lodGroups) {
      lod.update(camera);
    }
  }

  /** Get the number of managed LOD groups. */
  getGroupCount(): number {
    return this.lodGroups.length;
  }

  /** Dispose all LOD group geometries. */
  dispose(): void {
    for (const lod of this.lodGroups) {
      // Dispose only LOD1/LOD2 geometries (LOD0 is owned by the prop system)
      const levels = lod.levels;
      for (let i = 1; i < levels.length; i++) {
        const obj = levels[i].object;
        obj.traverse((child: THREE.Object3D) => {
          if (child instanceof THREE.Mesh) {
            child.geometry.dispose();
          }
        });
      }
    }
    this.lodGroups = [];
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

let lodSystem: PropLODSystem | null = null;

/** Get or create the global PropLODSystem instance. */
export function getPropLODSystem(quality?: GraphicsQuality): PropLODSystem {
  if (!lodSystem) {
    lodSystem = new PropLODSystem(quality || 'high');
  }
  return lodSystem;
}

/** Dispose the global PropLODSystem. */
export function disposePropLODSystem(): void {
  lodSystem?.dispose();
  lodSystem = null;
}
